import { sdkApi } from '../api';
import TCClient from 'client';
import { SearchTypes } from 'models/enums';
import { type SearchArgType, type SearchResults } from 'types/api';
import { Camelizer } from 'js-sdk/src/utils';
import type SearchResult from 'js-sdk/src/models/SearchResult';

const SECONDS_TO_EXPIRE_CACHE = 2;
const kwsPhase2EntityTypes = [
  SearchTypes.DISTRIBUTION_LIST,
  SearchTypes.GROUP,
  SearchTypes.MESSAGE,
  SearchTypes.TEAM,
  SearchTypes.USER,
  SearchTypes.FORUM,
];

const keywordSearchEntityListApi = sdkApi.injectEndpoints({
  endpoints: (build) => ({
    searchEntities: build.query<SearchResults<SearchResult>, SearchArgType>({
      queryFn: async ({ searchValue, orgId, continuation }, _queryApi) => {
        const res = await TCClient.search.keywordSearch({
          organizationId: orgId,
          version: 'SEARCH_PARITY',
          types: kwsPhase2EntityTypes,
          continuation: continuation || '',
          searchText: searchValue,
          pageSize: '50',
          resultsFormat: 'conversations',
        });

        const results = res.results as unknown as SearchResult[];
        return {
          data: {
            results: results,
            metadata: Camelizer.camelizeObject(res.metadata),
          },
        };
      },
      serializeQueryArgs: ({ queryArgs }) => {
        const { searchValue, orgId } = queryArgs;
        return { searchValue, orgId };
      },
      merge: (currentCache, newItems) => {
        if (newItems.metadata.firstHit === 1) {
          currentCache.results = newItems.results;
          currentCache.metadata = newItems.metadata;
        } else {
          const prevEntityTypes = currentCache.results.map((item) => item.entityType);
          const existingIds = new Set(currentCache.results.map((item) => item.entity.id));

          const entityTypeMap = new Map(
            currentCache.results.map((item) => [item.entityType, item])
          );

          newItems.results.forEach((newItem) => {
            const existingItem = entityTypeMap.get(newItem.entityType);

            if (existingItem && !existingIds.has(newItem.entity.id)) {
              currentCache.results.push(newItem);
              existingIds.add(newItem.entity.id);
            } else if (
              !prevEntityTypes.includes(newItem.entityType) &&
              !existingIds.has(newItem.entity.id)
            ) {
              currentCache.results.push(newItem);
              entityTypeMap.set(newItem.entityType, newItem);
              prevEntityTypes.push(newItem.entityType);
              existingIds.add(newItem.entity.id);
            }
          });
          currentCache.metadata = newItems.metadata;
        }
      },
      forceRefetch({ currentArg, previousArg }) {
        return true;
      },
      keepUnusedDataFor: SECONDS_TO_EXPIRE_CACHE,
    }),
  }),
  overrideExisting: true,
});

export const { useSearchEntitiesQuery } = keywordSearchEntityListApi;
